import React from "react";
import { Box, Button, Typography } from "@mui/material";
import { STATUS, TASK, TASK_DATE } from "../../constants";
import ImageAndText from "../ImageAndText/ImageAndText";
import highPriority from "../../assets/svgs/high.svg";
import mediumPriority from "../../assets/svgs/medium.svg";
import lowPriority from "../../assets/svgs/low.svg";
import clockIcon from "../../assets/svgs/clock-icon.svg";
import inProgressIcon from "../../assets/svgs/in-progress.svg";
import reScheduleIcon from "../../assets/svgs/re-schedule.svg";
import activeIcon from "../../assets/svgs/active-icon.svg";
import { MyTasksCalendarCardStyles } from "./MyTasksCalendarCardStyles";
import { capitalizeFirstLetter } from "../../utils/Utility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

interface MyTasksCalendarCardProps {
  data: {
    customerName: string;
    description: string;
    dueDate: string;
    dueTime: string;
    policyNumber: string;
    priority: string | null;
    status: string;
    taskName: string;
    timeStamp?: string;
    id: string;
    system: string;
    carrier: string;
  };
  handleEditTask: () => void;
  handleRescheduleTask: () => void;
  handleMarkAsComplete: () => void;
  handleDeleteTask: () => void;
}

const MyTasksCalendarCard: React.FC<MyTasksCalendarCardProps> = ({
  data,
  handleEditTask,
  handleRescheduleTask,
  handleMarkAsComplete,
  handleDeleteTask,
}) => {
  const renderPriority = (priority: string | null) => {
    let priorityText = "Unknown Priority";
    let priorityImage = lowPriority; // Default icon for unknown priority.

    switch (priority?.toLowerCase()) {
      case "high":
        priorityText = "High Priority";
        priorityImage = highPriority;
        break;
      case "unknown":
        priorityText = "High Priority";
        priorityImage = highPriority;
        break;
      case "medium":
        priorityText = "Medium Priority";
        priorityImage = mediumPriority;
        break;
      case "low":
        priorityText = "Low Priority";
        priorityImage = lowPriority;
        break;
    }

    return (
      <ImageAndText
        additionalClassName={MyTasksCalendarCardStyles.priorityContainer}
        image={priorityImage}
        text={priorityText}
      />
    );
  };

  const renderStatus = (status: string) => {
    let statusText = "Unknown Status";
    let statusImage = clockIcon; // Default icon for unknown status.

    switch (status && status.toLowerCase()) {
      case "pending":
        statusText = "Pending";
        statusImage = clockIcon;
        break;
      case "active":
        statusText = "Active";
        statusImage = activeIcon;
        break;
      case "in progress":
        statusText = "In Progress";
        statusImage = inProgressIcon;
        break;
      case "rescheduled":
        statusText = "Rescheduled";
        statusImage = reScheduleIcon;
        break;
      case "open":
        statusText = "Open";
        statusImage = clockIcon;
        break;
      case "completed":
        statusText = "Completed";
        statusImage = activeIcon;
        break;
    }

    return (
      <ImageAndText
        additionalClassName={MyTasksCalendarCardStyles.statusContainer}
        image={statusImage}
        text={statusText}
      />
    );
  };

  return (
    <Box 
      sx={MyTasksCalendarCardStyles.container}
      id="task-calendar-card"
      data-test-id="test-task-calendar-card"
      aria-label="Task Calendar Card"
    >
      {/* Header */}
      <Box 
        sx={MyTasksCalendarCardStyles.priorityHeadingContainer}
        id="task-card-header"
        data-test-id="test-task-card-header"
        aria-label="Task Card Header"
      >
        <Box 
          sx={MyTasksCalendarCardStyles.headingContainer}
          id="customer-info"
          data-test-id="test-customer-info"
          aria-label="Customer Information"
        >
          <Typography 
            sx={MyTasksCalendarCardStyles.heading}
            id="customer-name"
            data-test-id="test-customer-name"
            aria-label="Customer Name"
          >
            {capitalizeFirstLetter(data.customerName)}
          </Typography>
          <Box>
            <Typography 
              sx={MyTasksCalendarCardStyles.subheading}
              id="policy-number"
              data-test-id="test-policy-number"
              aria-label="Policy Number"
            >
              {data.policyNumber || "Policy Number N/A"}
            </Typography>
          </Box>
        </Box>

        <Box 
          id="priority-indicator"
          data-test-id="test-priority-indicator"
          aria-label="Task Priority"
        >
          {renderPriority(data.priority)}
        </Box>
      </Box>

      {/* Lower Section */}
      <Box 
        sx={MyTasksCalendarCardStyles.lowerContainer}
        id="task-details"
        data-test-id="test-task-details"
        aria-label="Task Details"
      >
        <Box 
          sx={MyTasksCalendarCardStyles.clientDetails}
          id="client-details"
          data-test-id="test-client-details"
          aria-label="Client Details"
        >
          <Box 
            sx={MyTasksCalendarCardStyles.headingContainer}
            id="status-section"
            data-test-id="test-status-section"
            aria-label="Task Status"
          >
            <Typography 
              sx={MyTasksCalendarCardStyles.heading}
              id="status-heading"
              data-test-id="test-status-heading"
              aria-label="Status Heading"
            >
              {STATUS}
            </Typography>
            <Box 
              id="status-indicator"
              data-test-id="test-status-indicator"
              aria-label="Status Indicator"
            >
              {renderStatus(data.status)}
            </Box>
          </Box>
          <Box 
            sx={MyTasksCalendarCardStyles.headingContainer}
            id="date-section"
            data-test-id="test-date-section"
            aria-label="Task Date"
          >
            <Typography 
              sx={MyTasksCalendarCardStyles.heading}
              id="date-heading"
              data-test-id="test-date-heading"
              aria-label="Date Heading"
            >
              {TASK_DATE}
            </Typography>
            <Box>
              <Typography 
                sx={MyTasksCalendarCardStyles.subheading}
                id="task-date-time"
                data-test-id="test-task-date-time"
                aria-label="Task Date and Time"
              >
                {data?.timeStamp
                  ? `${data?.timeStamp}`
                  : `${data.dueDate} ${data.dueTime}` || "N/A"}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box 
          sx={MyTasksCalendarCardStyles.headingContainer}
          id="task-name-section"
          data-test-id="test-task-name-section"
          aria-label="Task Name"
        >
          <Typography 
            sx={MyTasksCalendarCardStyles.heading}
            id="task-heading"
            data-test-id="test-task-heading"
            aria-label="Task Heading"
          >
            {TASK}
          </Typography>
          <Box>
            <Typography 
              sx={MyTasksCalendarCardStyles.subheading}
              id="task-name"
              data-test-id="test-task-name"
              aria-label="Task Name"
            >
              {capitalizeFirstLetter(data.taskName) || "N/A"}
            </Typography>
          </Box>
        </Box>
        <Box 
          sx={MyTasksCalendarCardStyles.headingContainer}
          id="description-section"
          data-test-id="test-description-section"
          aria-label="Task Description"
        >
          <Typography 
            sx={MyTasksCalendarCardStyles.heading}
            id="description-heading"
            data-test-id="test-description-heading"
            aria-label="Description Heading"
          >
            {"Task Description"}
          </Typography>
          <Box>
            <Typography 
              sx={MyTasksCalendarCardStyles.subheading}
              id="task-description"
              data-test-id="test-task-description"
              aria-label="Task Description"
            >
              {capitalizeFirstLetter(data.description) || "N/A"}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box 
        sx={MyTasksCalendarCardStyles.bottomContainer}
        id="action-buttons"
        data-test-id="test-action-buttons"
        aria-label="Task Action Buttons"
      >
        <Button
          size="small"
          sx={{ paddingX: "1rem" }}
          onClick={handleMarkAsComplete}
          id="mark-complete-btn"
          data-test-id="test-mark-complete-btn"
          aria-label="Mark Task as Complete"
        >
         {data.status?.toLowerCase() === "completed" ? "Open": "Mark as Complete"}
        </Button>
        <Button
          size="small"
          sx={{ paddingX: "1rem" }}
          onClick={handleRescheduleTask}
          id="reschedule-btn"
          data-test-id="test-reschedule-btn"
          aria-label="Reschedule Task"
        >
          Reschedule Task
        </Button>
        <Button
          size="small"
          sx={{ paddingX: "1rem" }}
          startIcon={<EditIcon />}
          onClick={handleEditTask}
          id="edit-btn"
          data-test-id="test-edit-btn"
          aria-label="Edit Task"
        >
          Edit Task
        </Button>
        <Button
          size="small"
          sx={{ paddingX: "1rem" }}
          startIcon={<DeleteIcon />}
          onClick={handleDeleteTask}
          id="delete-btn"
          data-test-id="test-delete-btn"
          aria-label="Delete Task"
        >
          Delete Task
        </Button>
      </Box>
    </Box>
  );
};

export default MyTasksCalendarCard;
